import { useState, useEffect } from 'react';
import { AlertCircle } from 'lucide-react';
import { parseMediaUrl, type ParsedMedia } from './parseMediaUrl';
import { UploadedVideoPlayer } from './UploadedVideoPlayer';

import { useTranslation } from 'react-i18next';
import { BrandMark } from '@/components/ui/brand-loader';
interface YouTubeShortsPreviewProps {
  url?: string;
  mediaUrl?: string;
  mediaKind?: 'image' | 'video';
  caption?: string;
  hashtags?: string[];
  username?: string;
  profileImageUrl?: string | null;
  thumbnailUrl?: string | null;
  isLoading?: boolean;
  error?: string | null;
  /** Overrides the empty-state line, e.g. for upload-only surfaces where "Add YouTube URL" is wrong. */
  emptyLabel?: string;
}

/**
 * A blob: URL has no file extension, so parseMediaUrl calls every local upload a
 * video: an uploaded image rendered as a black frame with a play button that
 * failed to play. The caller knows the file's kind, so trust it for blobs.
 */
function parsePreviewMedia(url?: string, mediaUrl?: string, mediaKind?: 'image' | 'video'): ParsedMedia {
  if (mediaUrl?.startsWith('blob:') && mediaKind) {
    return { type: mediaKind, directUrl: mediaUrl };
  }
  return parseMediaUrl(url, mediaUrl);
}

/**
 * YouTube Shorts preview — 9:16 portrait aspect ratio.
 * Renders with a Shorts-style bottom overlay (username + caption + hashtags).
 */
export function YouTubeShortsPreview({
  url,
  mediaUrl,
  mediaKind,
  caption,
  hashtags,
  username = 'your_account',
  profileImageUrl,
  thumbnailUrl,
  isLoading,
  error,
  emptyLabel,
}: YouTubeShortsPreviewProps) {
    const { t } = useTranslation();
  const [parsed, setParsed] = useState(() => parsePreviewMedia(url, mediaUrl, mediaKind));
  const [mediaError, setMediaError] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(() => {
    setParsed(parsePreviewMedia(url, mediaUrl, mediaKind));
    setMediaError(false);
    setIsPlaying(false);
  }, [url, mediaUrl, mediaKind]);

  const aspect = 'aspect-[9/16]';
  const base = `${aspect} w-full rounded-lg overflow-hidden relative transition-all duration-300`;

  /** Bottom overlay — username, caption, hashtags */
  const Overlay = () => (
    <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 via-black/40 to-transparent pointer-events-none z-10">
      <div className="flex items-center gap-2 mb-1.5">
        {profileImageUrl ? (
          <img src={profileImageUrl} alt={username} className="size-7 rounded-full object-cover ring-1 ring-white/30" />
        ) : (
          <div className="size-7 rounded-full bg-red-600 flex items-center justify-center text-white text-[10px] font-bold ring-1 ring-white/30">
            {username.charAt(0).toUpperCase()}
          </div>
        )}
        <span className="text-xs font-semibold text-white">@{username}</span>
      </div>
      {caption && (
        <p className="text-xs text-white/90 line-clamp-2 leading-snug">{caption}</p>
      )}
      {hashtags && hashtags.length > 0 && (
        <p className="text-xs text-red-300 mt-1">
          {hashtags.slice(0, 3).map(t => `#${t}`).join(' ')}
        </p>
      )}
    </div>
  );

  /** Shorts badge in top-left corner */
  const ShortsBadge = () => (
    <div className="absolute top-2 left-2 z-20 flex items-center gap-1 bg-black/50 backdrop-blur-sm rounded-full px-2 py-0.5">
      {/* YouTube Shorts official "S" logo */}
      <svg width="10" height="12" viewBox="0 0 98.94 122.88" fill="none">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          fill="#FF0000"
          d="M63.49,2.71c11.59-6.04,25.94-1.64,32.04,9.83c6.1,11.47,1.65,25.66-9.94,31.7l-9.53,5.01 c8.21,0.3,16.04,4.81,20.14,12.52c6.1,11.47,1.66,25.66-9.94,31.7l-50.82,26.7c-11.59,6.04-25.94,1.64-32.04-9.83 c-6.1-11.47-1.65-25.66,9.94-31.7l9.53-5.01c-8.21-0.3-16.04-4.81-20.14-12.52c-6.1-11.47-1.65-25.66,9.94-31.7L63.49,2.71 L63.49,2.71z M36.06,42.53l30.76,18.99l-30.76,18.9V42.53L36.06,42.53z"
        />
      </svg>
      <span className="text-white text-[9px] font-semibold tracking-wide">Shorts</span>
    </div>
  );

  // Loading
  if (isLoading) {
    return (
      <div className={`${base} bg-black flex items-center justify-center`}>
        <div className="flex flex-col items-center gap-3">
          <BrandMark size={40} variant="loading" />
          <p className="text-white/60 text-xs">{t('preview.loadingShort')}</p>
        </div>
      </div>
    );
  }

  // Error
  if (error && !parsed.embedUrl && !mediaUrl) {
    return (
      <div className={`${base} bg-gray-100 dark:bg-gray-900 flex flex-col items-center justify-center gap-3`}>
        <AlertCircle className="size-8 text-red-500" />
        <p className="text-xs text-gray-500 text-center px-4">{error}</p>
      </div>
    );
  }

  // Uploaded image
  if (parsed.type === 'image' && parsed.directUrl && !mediaError) {
    return (
      <div className={`${base} bg-black`}>
        <img src={parsed.directUrl} alt={t('preview.uploadedContent')} className="w-full h-full object-cover" onError={() => setMediaError(true)} />
        <ShortsBadge />
        <Overlay />
      </div>
    );
  }

  // Uploaded video
  if (parsed.type === 'video' && parsed.directUrl && !mediaError) {
    if (isPlaying) {
      return (
        <div className={`${base} bg-black`}>
          <UploadedVideoPlayer src={parsed.directUrl} className="absolute inset-0 w-full h-full object-cover" autoPlay muted />
          <ShortsBadge />
          <Overlay />
        </div>
      );
    }
    return (
      <div className={`${base} bg-black group cursor-pointer`} onClick={() => setIsPlaying(true)}>
        {thumbnailUrl ? (
          <img src={thumbnailUrl} alt={t('preview.videoThumbnail')} className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-b from-gray-800 to-gray-900" />
        )}
        <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors" />
        <div className="absolute inset-0 flex items-center justify-center z-20">
          <div className="size-16 rounded-full bg-white/90 dark:bg-black/70 flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform">
            <svg className="size-8 text-gray-800 dark:text-white ml-1" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
          </div>
        </div>
        <ShortsBadge />
        <Overlay />
      </div>
    );
  }

  // YouTube Shorts embed
  if (parsed.type === 'youtube' && parsed.embedUrl) {
    if (isPlaying) {
      return (
        <div className={`${base} bg-black`}>
          <iframe
            src={`${parsed.embedUrl}?autoplay=1`}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title="YouTube Short"
          />
          <ShortsBadge />
        </div>
      );
    }
    
    // Extract video ID from embedUrl to construct a YouTube thumbnail
    const ytIdMatch = parsed.embedUrl.match(/\/embed\/([a-zA-Z0-9_-]+)/);
    const fallbackPoster = ytIdMatch ? `https://img.youtube.com/vi/${ytIdMatch[1]}/hqdefault.jpg` : null;
    const poster = thumbnailUrl || fallbackPoster;

    return (
      <div className={`${base} bg-black group cursor-pointer`} onClick={() => setIsPlaying(true)}>
        {poster ? (
          <img src={poster} alt={t('preview.youtubeThumbnail')} className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-b from-gray-800 to-gray-900" />
        )}
        <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors" />
        <div className="absolute inset-0 flex items-center justify-center z-20">
          <div className="size-16 rounded-full bg-white/90 flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform">
            <svg className="size-8 text-red-600 ml-1" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
          </div>
        </div>
        <ShortsBadge />
        <Overlay />
      </div>
    );
  }

  // Placeholder
  return (
    <div className={`${base} bg-gradient-to-b from-gray-900 via-gray-800 to-black flex flex-col items-center justify-center gap-3 group cursor-pointer`}>
      <ShortsBadge />
      {/* Shorts "S" logo */}
      <div className="size-16 rounded-full bg-red-600/20 border border-red-500/30 flex items-center justify-center group-hover:scale-110 transition-transform">
        <svg width="28" height="34" viewBox="0 0 98.94 122.88" fill="none">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            fill="#FF4444"
            d="M63.49,2.71c11.59-6.04,25.94-1.64,32.04,9.83c6.1,11.47,1.65,25.66-9.94,31.7l-9.53,5.01 c8.21,0.3,16.04,4.81,20.14,12.52c6.1,11.47,1.66,25.66-9.94,31.7l-50.82,26.7c-11.59,6.04-25.94,1.64-32.04-9.83 c-6.1-11.47-1.65-25.66,9.94-31.7l9.53-5.01c-8.21-0.3-16.04-4.81-20.14-12.52c-6.1-11.47-1.65-25.66,9.94-31.7L63.49,2.71 L63.49,2.71z M36.06,42.53l30.76,18.99l-30.76,18.9V42.53L36.06,42.53z"
          />
        </svg>
      </div>
      <div className="text-center">
        <p className="text-sm font-medium text-gray-300">{emptyLabel ?? t('preview.addYouTubeUrl')}</p>
        <p className="text-xs text-gray-500 mt-0.5">9:16 · Shorts format</p>
      </div>
      <Overlay />
    </div>
  );
}
